/**
 * Read-only document viewer for phones, native, and any client without the
 * desktop-web document editor.
 *
 * File.serialize() strips `documentSourceMarkdown` to keep chat/file list
 * payloads small, so this viewer loads the Markdown via
 * GET /json/files/:id/document (same source as the desktop editor). Inline
 * markdown on the file object is still honored when present (tests / older
 * payloads). Rendering goes through react-native-markdown-display, the same
 * engine the chat uses for assistant messages.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Linking, Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import Markdown from 'react-native-markdown-display';
import FitImage from 'react-native-fit-image';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { openFileExternally } from '@/constants/utils/files';
import { filesApi } from '@/lib/api';
import { documentPreviewMarkdown } from '@/lib/documentPreviewMarkdown';
import { createFileOpenGuard } from '@/lib/fileOpenSession';

const CONTENT_MAX_WIDTH = 760;
const SAFE_MARKDOWN_URL = /^(https?:|mailto:)/i;

function MacDocumentTable({ node, children, style }) {
  const [width, setWidth] = useState(0);
  const rows = (node.children || []).flatMap(section => section.type === 'tr' ? [section] : section.children || []);
  const columns = Math.max(1, ...rows.filter(row => row.type === 'tr').map(row => row.children?.length || 0));
  return <ScrollView horizontal showsHorizontalScrollIndicator accessibilityLabel="Document table"
    style={{ width: '100%', flexGrow: 0 }}
    onLayout={event => setWidth(event.nativeEvent.layout.width)}>
    <View style={[style, { width: Math.max(width, columns * 160) }]}>{children}</View>
  </ScrollView>;
}

function inlineMarkdown(file) {
  return typeof file?.parameters?.documentSourceMarkdown === 'string'
    ? file.parameters.documentSourceMarkdown.trim()
    : '';
}

export default function DocumentViewer({ file, onSourceLoaded }) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const linkColor = colors.text_color_link
    || (isDark ? '#58a6ff' : '#3157a4');
  const fileId = file?.id || file?._id || null;
  const seeded = inlineMarkdown(file);
  const [markdown, setMarkdown] = useState(seeded);
  const [loadStatus, setLoadStatus] = useState(seeded ? 'ready' : (fileId ? 'loading' : 'empty'));
  const [retryKey, setRetryKey] = useState(0);
  const previewMarkdown = useMemo(() => Platform.OS === 'macos' ? documentPreviewMarkdown(markdown) : markdown, [markdown]);

  useEffect(() => {
    const nextSeeded = inlineMarkdown(file);
    if (nextSeeded) {
      setMarkdown(nextSeeded);
      setLoadStatus('ready');
      onSourceLoaded?.(file, { markdown: nextSeeded, pdfOptions: file?.parameters?.pdfOptions });
      return undefined;
    }

    onSourceLoaded?.(file, null);
    if (!fileId) {
      setMarkdown('');
      setLoadStatus('empty');
      return undefined;
    }

    let cancelled = false;
    const isRequestCurrent = createFileOpenGuard(() => !cancelled);
    setLoadStatus('loading');
    filesApi.fetchEditableDocument(fileId, { isRequestCurrent })
      .then((document) => {
        if (!isRequestCurrent()) return;
        const body = typeof document?.markdown === 'string' ? document.markdown.trim() : '';
        setMarkdown(body);
        setLoadStatus(body ? 'ready' : 'empty');
        onSourceLoaded?.(file, body ? { markdown: body, pdfOptions: document?.pdfOptions } : null);
      })
      .catch(() => {
        if (isRequestCurrent()) {
          setMarkdown('');
          setLoadStatus('error');
        }
      });

    return () => { cancelled = true; };
  }, [file, fileId, onSourceLoaded, retryKey]);

  const styles = useMemo(() => StyleSheet.create({
    container: { flex: 1 },
    content: {
      paddingHorizontal: 20,
      paddingVertical: 24,
      width: '100%',
      maxWidth: CONTENT_MAX_WIDTH,
      alignSelf: 'center',
    },
    empty: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 10, padding: 32 },
    downloadButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      marginTop: 6,
      paddingHorizontal: 16,
      paddingVertical: 10,
      borderRadius: 8,
      backgroundColor: colors.solid_color_secondary,
    },
  }), [colors]);

  const markdownStyles = useMemo(() => ({
    body: {
      color: colors.text_color_primary,
      fontSize: 17,
      lineHeight: 25.5,
      ...(Platform.OS === 'web' && {
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        userSelect: 'text',
      }),
    },
    paragraph: { marginTop: 6, marginBottom: 6 },
    heading1: {
      color: colors.text_color_primary,
      fontSize: 28,
      fontWeight: '600',
      lineHeight: 34,
      marginTop: 14,
      marginBottom: 8,
    },
    heading2: {
      color: colors.text_color_primary,
      fontSize: 24,
      fontWeight: '600',
      lineHeight: 30,
      marginTop: 14,
      marginBottom: 6,
    },
    heading3: {
      color: colors.text_color_primary,
      fontSize: 20,
      fontWeight: '600',
      lineHeight: 27,
      marginTop: 12,
      marginBottom: 5,
    },
    heading4: { color: colors.text_color_primary, fontSize: 18, fontWeight: '600', marginTop: 10, marginBottom: 4 },
    heading5: { color: colors.text_color_primary, fontSize: 17, fontWeight: '600', marginTop: 8, marginBottom: 4 },
    heading6: { color: colors.text_color_primary, fontSize: 17, fontWeight: '500', marginTop: 8, marginBottom: 4 },
    hr: {
      backgroundColor: colors.border_color_secondary,
      height: 1,
      marginTop: 16,
      marginBottom: 16,
    },
    blockquote: {
      backgroundColor: 'transparent',
      borderLeftColor: colors.solid_color_secondary,
      borderLeftWidth: 4,
      paddingLeft: 12,
      paddingRight: 12,
      paddingTop: 8,
      paddingBottom: 8,
      marginTop: 8,
      marginBottom: 8,
    },
    bullet_list: { marginTop: 6, marginBottom: 6 },
    ordered_list: { marginTop: 6, marginBottom: 6 },
    list_item: { marginTop: 4, marginBottom: 4 },
    bullet_list_icon: { color: colors.text_color_primary, marginRight: 8 },
    ordered_list_icon: { color: colors.text_color_primary, marginRight: 8 },
    code_inline: {
      ...(Platform.OS === 'macos' ? { fontFamily: 'Menlo' } : {}),
      backgroundColor: colors.solid_color_secondary,
      color: colors.text_color_primary,
      paddingHorizontal: 6,
      paddingVertical: 2,
      borderRadius: 4,
      fontSize: 15,
    },
    fence: {
      ...(Platform.OS === 'macos' ? { fontFamily: 'Menlo' } : {}),
      backgroundColor: colors.solid_color_secondary,
      color: colors.text_color_primary,
      borderWidth: 0,
      padding: 12,
      borderRadius: 8,
      marginTop: 8,
      marginBottom: 8,
      fontSize: 15,
    },
    code_block: {
      ...(Platform.OS === 'macos' ? { fontFamily: 'Menlo' } : {}),
      backgroundColor: colors.solid_color_secondary,
      color: colors.text_color_primary,
      borderWidth: 0,
      padding: 12,
      borderRadius: 8,
      marginTop: 8,
      marginBottom: 8,
      fontSize: 15,
    },
    table: {
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      borderRadius: 6,
      marginTop: 8,
      marginBottom: 8,
    },
    thead: { backgroundColor: colors.solid_color_secondary },
    th: { padding: 8, fontWeight: '600' },
    tr: {
      borderBottomWidth: 1,
      borderColor: colors.border_color_secondary,
      flexDirection: 'row',
    },
    td: { padding: 8 },
    link: {
      color: linkColor,
      textDecorationLine: 'underline',
      textDecorationColor: linkColor,
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    },
    image: { borderRadius: 6, marginTop: 8, marginBottom: 8 },
  }), [colors, linkColor]);

  const onLinkPress = useCallback((url) => {
    if (!url) return false;
    const trimmed = String(url).trim();
    if (!SAFE_MARKDOWN_URL.test(trimmed)) return false;
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.open(trimmed, '_blank', 'noopener,noreferrer');
    } else {
      Linking.openURL(trimmed).catch(() => {});
    }
    // Returning false tells react-native-markdown-display not to run its own
    // default Linking handler on top of ours.
    return false;
  }, []);

  const markdownRules = useMemo(() => ({
    ...(Platform.OS === 'macos' ? {
      table: (node, children, parent, styles) => <MacDocumentTable key={node.key} node={node} style={styles._VIEW_SAFE_table}>{children}</MacDocumentTable>,
      image: (node, children, parent, styles, allowedImageHandlers, defaultImageHandler) => {
      const { src, alt } = node.attributes;
      const allowed = allowedImageHandlers.some(prefix => src.toLowerCase().startsWith(prefix.toLowerCase()));
      if (!allowed && defaultImageHandler === null) return null;
      // The dependency spreads key into JSX, which triggers a development
      // overlay on every image. Keep its sizing and URL policy unchanged.
      return <FitImage key={node.key} indicator style={styles._VIEW_SAFE_image}
        source={{ uri: allowed ? src : `${defaultImageHandler}${src}` }}
        {...(alt ? { accessible: true, accessibilityLabel: alt } : {})} />;
    } } : {}),
    link: (node, children) => {
      const rawUrl = node.attributes?.href;
      const url = typeof rawUrl === 'string' ? rawUrl.trim() : '';
      if (Platform.OS === 'web') {
        const isSafe = SAFE_MARKDOWN_URL.test(url);
        return (
          <a
            key={node.key}
            href={isSafe ? url : undefined}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => {
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1) {
                return;
              }
              event.preventDefault();
              onLinkPress(url);
            }}
            style={{
              color: linkColor,
              textDecoration: 'underline',
              cursor: isSafe ? 'pointer' : 'default',
              wordBreak: 'break-word',
            }}
          >
            {children}
          </a>
        );
      }
      return (
        <Text
          key={node.key}
          style={markdownStyles.link}
          onPress={() => onLinkPress(url)}
          accessibilityRole="link"
          {...(Platform.OS === 'macos' ? { focusable: SAFE_MARKDOWN_URL.test(url), disabled: !SAFE_MARKDOWN_URL.test(url) } : {})}
        >
          {children}
        </Text>
      );
    },
  }), [linkColor, markdownStyles.link, onLinkPress]);

  if (loadStatus === 'loading') {
    return (
      <View style={styles.empty}>
        <ActivityIndicator color={colors.text_color_secondary} />
        <Text color="secondary">Loading document…</Text>
      </View>
    );
  }

  if (loadStatus !== 'ready' || !markdown) {
    return (
      <View style={styles.empty}>
        <Icon name="file-word" size={30} color={colors.text_color_tertiary} />
        <Text color="secondary" style={{ textAlign: 'center' }}>
          {loadStatus === 'error'
            ? "Couldn't load this document preview."
            : "This document doesn't have a preview yet."}
        </Text>
        {loadStatus === 'error' && <Pressable
          accessibilityRole="button"
          accessibilityLabel="Retry"
          onPress={() => setRetryKey(value => value + 1)}
          style={styles.downloadButton}
        >
          <Icon name="rotate-right" size={14} color={colors.text_color_primary} />
          <Text size="s">Retry</Text>
        </Pressable>}
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Download document"
          onPress={() => openFileExternally(file)}
          style={styles.downloadButton}
        >
          <Icon name="arrow-down-to-line" size={14} color={colors.text_color_primary} />
          <Text size="s">Download</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={Platform.OS === 'macos' ? { alignItems: 'center' } : styles.content}>
      <View style={Platform.OS === 'macos' ? styles.content : undefined}>
        <Markdown style={markdownStyles} rules={markdownRules} onLinkPress={onLinkPress}>
          {previewMarkdown}
        </Markdown>
      </View>
    </ScrollView>
  );
}
